import { Box, Button } from "@mui/material";
import StyledDialog from "../../../../components/StyledDialog";
import { DisclosureHost } from "../../../../hooks/use-disclosure";
import { Form } from "react-formio";
import { useEffect, useMemo, useState } from "react";
import { useFormBuilderByUuid } from "../../../../queries/form-builder/use-form-builder-by-uuid";
import { isEqual } from "lodash";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import { usePatientIntakeFormUpdate } from "../../../../queries/patient-portal/forms/use-patient-intake-form-update";
import { getErrorSnackbar, getSuccessSnackbar } from "../../../../utils/utils";
import { useQueryClient } from "@tanstack/react-query";
import { getRealm } from "../../../../AuthProvider";
import { useQueryParams } from "../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../hooks/useSnackbar";
import { getViewAs, getStoredRealm } from "@/utils/auth-storage";

interface UpdatePatientIntakeFormProps {
  disclosureHost: DisclosureHost;
  isViewForm: boolean;
  publicView?: boolean;
}

const DEFAULT_ERR_TOAST: any = {
  type: "error",
  message: "Something went wrong",
};

const UpdatePatientIntakeForm = ({
  disclosureHost,
  isViewForm,
  publicView,
}: UpdatePatientIntakeFormProps) => {
  const hostNameArr = location.hostname?.split(".");
  const realm = getRealm(hostNameArr);

  const [schema, setSchema] = useState<any>({});

  const showSnackbar = useSnackbar();
  const [intakeFormData, setIntakeFormData] = useState();
  const queryClient = useQueryClient();

  const memoizedSchema = useMemo(() => schema, [schema]);
  const patientId = useQueryParams("patientId");

  const isPatient = getViewAs() === "patient";
  const xTENANTID = isPatient ? getStoredRealm() : undefined;
  const user = sessionStorage.getItem("user");
  const patientData: any = user ? JSON.parse(user) : undefined;

  const { mutateAsync: updateIntakeForm } =
    usePatientIntakeFormUpdate(publicView);

  const { data: formComponentData, isLoading: isLoadingFormData } =
    useFormBuilderByUuid({
      customFormId: disclosureHost.data?.formId,
      xTENANTID: publicView ? realm : (xTENANTID ?? undefined),
      publicView,
    });

  useEffect(() => {
    if (formComponentData) {
      fetchFormContent(
        isViewForm ? disclosureHost.data : formComponentData?.data
      ).then((schema) => {
        setSchema(schema ?? {});
      });
    }
  }, [formComponentData, disclosureHost.data]);

  async function fetchFormContent(selectedForm: any): Promise<any> {
    if (!selectedForm?.content) return;

    return await fetch(selectedForm?.content)
      .then((response) => response?.json())
      .then((urlData) => {
        if (isViewForm) setIntakeFormData(urlData?.data);

        return {
          name: selectedForm.name,
          components: urlData?.components,
        };
      })
      .catch(() => {
        showSnackbar(getErrorSnackbar(DEFAULT_ERR_TOAST));
      });
  }

  const handelCloseAndResetForm = () => {
    disclosureHost.close();
    setIntakeFormData(undefined);

    queryClient.invalidateQueries({ queryKey: ["patient-intake-form-list"] });

    setSchema({});
  };

  const handleEditDataChange = (event: any) => {
    const newFormData = event.data;

    // Compare new form data with the current form data and previous form data
    if (isEqual(newFormData, intakeFormData)) {
      setIntakeFormData(newFormData);
    }
  };

  const handleUpdateIntakeForm = () => {
    const patient = {
      [publicView ? patientId : patientData?.uuid]:
        (patientData?.firstName ?? "") + " " + (patientData?.lastName ?? ""),
    };

    const formObj = {
      components: schema?.components ?? [],
      data: intakeFormData,
    };

    const currentFormName = (formComponentData?.data as any)?.name;

    const payload = {
      patient,
      content: JSON.stringify(formObj),
      uuid: disclosureHost.data?.uuid,
      formId: disclosureHost.data?.formId,
      formName: currentFormName,
      filledForms: currentFormName ?? "",
    };

    updateIntakeForm({
      patientIntakeForm: payload,
      xTENANTID: publicView ? realm : (xTENANTID ?? undefined),
    })
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
        setTimeout(handelCloseAndResetForm, 1500);
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  return (
    <StyledDialog
      open={disclosureHost.opened}
      onClose={disclosureHost.close}
      dialogTitle={disclosureHost.data?.formName}
      dialogAction={
        <Button variant="contained" onClick={handleUpdateIntakeForm} autoFocus>
          {isViewForm ? "Update" : "submit"}
        </Button>
      }
    >
      <Box width={"55vw"}>
        <LoadingOverlay isLoading={isLoadingFormData} />

        <Form
          form={memoizedSchema}
          onChange={(e: any) => {
            if (isViewForm) handleEditDataChange(e);
            else setIntakeFormData(e?.data);
          }}
          submission={intakeFormData ? { data: intakeFormData } : { data: {} }}
        />
      </Box>
    </StyledDialog>
  );
};

export default UpdatePatientIntakeForm;
